import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';

import * as _ from 'lodash';
import { Subject, asyncScheduler, map, takeUntil, throttleTime } from 'rxjs';
import { SelectedVehiclesDictionary } from 'src/app/modules/core/models/custom-dictionary';
import { VehicleStore } from 'src/app/modules/core/store';
import { WidgetsService } from '@components/widgets/widgets.service';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { CompactListColumn, CompactListItem, DeviationFilter } from 'src/app/modules/shared/models';
import { Vehicle } from 'src/app/modules/shared/models/vehicle';

import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackTextComponent } from '../../../../modules/shared/components/feedback-text/feedback-text.component';
import { SpinnerComponent } from '../../../../modules/shared/components/spinner/spinner.component';
import { CompactListComponent } from '../../../../modules/shared/components/compact-list/compact-list.component';
import { DeviationFilterComponent } from '../../../../modules/shared/components/deviation-filter/deviation-filter.component';
import { ToggleViewComponent } from '../../../../modules/shared/components/toggle-view/toggle-view.component';
import { MatBadgeModule } from '@angular/material/badge';
import { DecimalPipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

enum FilterStopValue {
    all = 'all',
    timingPoint = 'timingPoint',
    startStop = 'startStop'
}

@Component({
    selector: 'app-punctuality-selection-overview',
    templateUrl: './punctuality-selection-overview.component.html',
    styleUrls: ['./punctuality-selection-overview.component.scss'],
    imports: [
        MatCheckboxModule, ReactiveFormsModule, FormsModule, MatBadgeModule, 
        MatRadioModule, ToggleViewComponent, DeviationFilterComponent, CompactListComponent, 
        SpinnerComponent, FeedbackTextComponent, DecimalPipe, TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})

export class PunctualitySelectionOverviewComponent implements OnInit, OnDestroy {
    private _vehicleStore = inject(VehicleStore);
    private _router = inject(Router);
    private _widgetsService = inject(WidgetsService);
    private _elementRef = inject(ElementRef);

    public isLoading = false;
    public columnsLate: CompactListColumn[];
    public columnsEarly: CompactListColumn[];

    public itemsLate: CompactListItem[];
    public itemsEarly: CompactListItem[];

    public earlyDeviationFilter = new DeviationFilter(false);
    public lateDeviationFilter = new DeviationFilter(false);

    public filterInMap: boolean;

    public filterStopOptions: any[] = [
        {
            value: FilterStopValue.all,
            title: _translate('All')
        },
        {
            value: FilterStopValue.startStop,
            title: _translate('Starting stations')
        }
    ];

    public stopFilterValue: FilterStopValue = FilterStopValue.all;
    public showStopFilter = false;

    private _stateKey = 'punctuality-selection-overview-component-state';
    private _ngUnsubscribe$ = new Subject<void>();
    private _selectedVehicles: SelectedVehiclesDictionary;
    private _rawItemsEarly: CompactListItem[];
    private _rawItemsLate: CompactListItem[];

    // Set this if we detect any timingPoint from the data
    private _filterStopOptions: any[] = [
        {
            value: FilterStopValue.all,
            title: _translate('All')
        },
        {
            value: FilterStopValue.timingPoint,
            title: _translate('Regulated stops')
        },
        {
            value: FilterStopValue.startStop,
            title: _translate('Starting stations')
        }
    ];
    private _hasSetFilterStopOptions = false;


    constructor() {
        this._initState();
    }

    @HostListener('document:click', ['$event']) componentClick(event) {
        if (!this._elementRef.nativeElement.contains(event.target)) {
            // Hide stopFilter if click is outside component
            this.showStopFilter = false;
        }
    }

    ngOnInit() {
        this._subscribeToData();
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        this._vehicleStore.setInventoryFilter(undefined);
    }

    public get filterIsActive() {
        return this.stopFilterValue !== FilterStopValue.all;
    }

    onEarlyDeviationFilterChanged(deviationsFilter: DeviationFilter) {
        this.earlyDeviationFilter = deviationsFilter;
        this._updateState();
    }
    onLateDeviationFilterChanged(deviationsFilter: DeviationFilter) {
        this.lateDeviationFilter = deviationsFilter;
        this._updateState();
    }

    public itemSelected(item: any) {
        const encodedItemId = encodeURIComponent(item.id);
        const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right0:vehicle/${encodedItemId}`);
        if (indexOfItemId === -1) {
            this._widgetsService.openVehicle(String(item.id));
        }
    }

    public columnsUpdated(columns: CompactListColumn[]) {
        this._updateState();
    }

    public setMapFilter(event: MatCheckboxChange) {
        this.filterInMap = event.checked;
    }

    public setStopFilter(event: MatRadioChange) {
        this._updateState();
    }

    public toggleStopFilter() {
        this.showStopFilter = !this.showStopFilter;
    }

    private _applyFilter() {
        if (!this._rawItemsEarly && !this._rawItemsLate) {
            // Defer assignment to prevent ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
                this.itemsEarly = undefined;
                this.itemsLate = undefined;
                this._vehicleStore.setInventoryFilter(undefined);
            }, 0);
            return;
        }

        const selectedEarlyDeviationTotalSeconds = this.earlyDeviationFilter.totalSeconds;
        const filteredEarly = !this._rawItemsEarly ? undefined : _.filter(this._rawItemsEarly, (item) => 
            !selectedEarlyDeviationTotalSeconds || item.object.departureDeviation.deviation.getTotalSeconds() >= selectedEarlyDeviationTotalSeconds);

        const selectedLateDeviationTotalSeconds = this.lateDeviationFilter.totalSeconds;
        const filteredLate = !this._rawItemsLate ? undefined : _.filter(this._rawItemsLate, (item) => 
            !selectedLateDeviationTotalSeconds || item.object.departureDeviation.deviation.getTotalSeconds() <= (selectedLateDeviationTotalSeconds * -1));

        // apply filter on stop
        const finalEarly = !filteredEarly ? undefined : this._filterListOnStop(filteredEarly);
        const finalLate = !filteredLate ? undefined : this._filterListOnStop(filteredLate);

        // Defer assignment to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.itemsEarly = finalEarly;
            this.itemsLate = finalLate;
            
            // set the inventory filter on map if the filter in selected
            let filteredInventoryNumbers: string[];
            if (this.filterInMap) {
                filteredInventoryNumbers = _.map(this.itemsEarly, (item) => item.properties['inventoryNumber']);
                filteredInventoryNumbers = _.concat(filteredInventoryNumbers,
                    _.map(this.itemsLate, (item) => item.properties['inventoryNumber']));
            }
            
            this._vehicleStore.setInventoryFilter(filteredInventoryNumbers);
        }, 0);
    }

    private _filterListOnStop(items: CompactListItem[]): CompactListItem[] {
        if (this.stopFilterValue === FilterStopValue.all) {
            return items;
        } else if (this.stopFilterValue === FilterStopValue.startStop) {
            return _.filter(items, (item: CompactListItem) => item.properties['isFirstStop'] === true);

        } else if (this.stopFilterValue === FilterStopValue.timingPoint) {
            return _.filter(items, (item: CompactListItem) => item.properties['isTimingPoint'] === true);
        }
        return items;
    }

    private _initState() {
        this._initColumnDefaults();
        this._initSelectedDeviationsDefaults();

        const stateData = localStorage.getItem(this._stateKey);
        if (stateData) {
            const state = Object.assign(new PunctualitySelectionOverviewComponentState(), JSON.parse(stateData));

            // Only update these settings if it exists in the stored state
            if (state.selectedEarlyDeviationMinutes && state.selectedEarlyDeviationSeconds) {
                this.earlyDeviationFilter.minutes = state.selectedEarlyDeviationMinutes;
                this.earlyDeviationFilter.seconds = state.selectedEarlyDeviationSeconds;
            }

            if (state.selectedLateDeviationMinutes && state.selectedLateDeviationSeconds) {
                this.lateDeviationFilter.minutes = state.selectedLateDeviationMinutes;
                this.lateDeviationFilter.seconds = state.selectedLateDeviationSeconds;
            }

            if (state.selectedStopFilter) {
                this.stopFilterValue = state.selectedStopFilter;
            } else {
                this.stopFilterValue = FilterStopValue.all;
            }

            this.columnsEarly = _.map(this.columnsEarly, (column) => {
                const storedColumn = _.find(state.columnsEarly, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.sortNumeric;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });
            this.columnsLate = _.map(this.columnsLate, (column) => {
                const storedColumn = _.find(state.columnsLate, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.sortNumeric;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });
        }
    }

    private _updateState() {
        let state = new PunctualitySelectionOverviewComponentState();
        state = {
            ...state,
            selectedEarlyDeviationMinutes: this.earlyDeviationFilter.minutes,
            selectedEarlyDeviationSeconds: this.earlyDeviationFilter.seconds,
            selectedLateDeviationMinutes: this.lateDeviationFilter.minutes,
            selectedLateDeviationSeconds: this.lateDeviationFilter.seconds,
            columnsEarly: this.columnsEarly,
            columnsLate: this.columnsLate,
            selectedStopFilter: this.stopFilterValue
        };
        localStorage.setItem(this._stateKey, JSON.stringify(state));
    }

    private _initColumnDefaults() {
        this.columnsLate = [
            ColumnFactory.getInventoryColumn(),
            ColumnFactory.getRouteNumberColumn({ width: 32 }),
            ColumnFactory.getDeviationColumn({ sort: 'desc' }),
            ColumnFactory.getNextStopColumn({ width: 65 }),
            ColumnFactory.getBearingColumn()
        ];

        this.columnsEarly = [
            ColumnFactory.getInventoryColumn(),
            ColumnFactory.getRouteNumberColumn({ width: 32 }),
            ColumnFactory.getDeviationColumn({ sort: 'desc' }),
            ColumnFactory.getNextStopColumn({ width: 65 }),
            ColumnFactory.getBearingColumn()
        ];
    }

    private _initSelectedDeviationsDefaults() {
        this.earlyDeviationFilter.minutes = 1;
        this.earlyDeviationFilter.seconds = 0;

        this.lateDeviationFilter.minutes = 3;
        this.lateDeviationFilter.seconds = 0;
    }

    private _subscribeToData() {
        this._vehicleStore.getVehiclesState().pipe(
            throttleTime(1000, asyncScheduler, { leading: true, trailing: true }),
            map(vehicles => {
                if (!vehicles) {
                    // needed for the spinner to show instead of empty data-feedback
                    return null;
                } else {
                    return _.chain(vehicles)
                        .filter(vehicle => !!vehicle.departureDeviation)
                        .map((vehicle: Vehicle) => {
                            const item = new CompactListItem<Vehicle>();
                            item.object = vehicle;
                            item.id = vehicle.inventoryNumber;
                            item.type = 'vehicle';
                            item.properties['inventoryNumber'] = vehicle.inventoryNumber;
                            item.properties['inventoryNumberWithoutPrefix'] = vehicle.inventoryNumberWithoutPrefix;
                            item.properties['garageCode'] = vehicle.garageCode;
                            item.properties['routeNumber'] = vehicle?.routeNumber;
                            item.properties['blockNumber'] = vehicle?.blockNumber;
                            item.properties['tripNumber'] = vehicle?.tripNumber;
                            item.properties['stop'] = vehicle.departureDeviation?.stopName;
                            item.properties['bearing'] = vehicle.position?.bearing;
                            item.properties['deviation'] = vehicle.departureDeviation?.deviation.toShortString();
                            item.properties['deviationseconds'] = vehicle.departureDeviation?.deviation.getTotalSeconds();
                            item.properties['color'] = vehicle.departureDeviation?.color;

                            item.properties['isFirstStop'] = vehicle.departureDeviation?.sequenceNumber === 1;
                            item.properties['isTimingPoint'] = vehicle.departureDeviation?.isTimingPoint === true;

                            if (this._selectedVehicles) {
                                item.selected = _.includes(this._selectedVehicles.allValues, item.id);
                            }
                            return item;
                        }).value();
                }
            }),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((items) => {
            if (items) {
                this._rawItemsEarly = _.filter(items, (item) => item.object.departureDeviation.deviation.getTotalSeconds() > 0);
                this._rawItemsLate = _.filter(items, (item) => item.object.departureDeviation.deviation.getTotalSeconds() <= 0);
            } else {
                this._rawItemsEarly = undefined;
                this._rawItemsLate = undefined;
            }

            // check if any item has isTimingPoint value set to true
            if (!this._hasSetFilterStopOptions) {
                const hasTimingPoint = _.find(items, (item: CompactListItem) => item.properties['isTimingPoint'] === true);
                if (hasTimingPoint) {
                    this.filterStopOptions = this._filterStopOptions;
                    this._hasSetFilterStopOptions = true;
                }
            }

            this._applyFilter();
        });

        this._vehicleStore.getSelectedVehiclesState().pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((selectedVehicles: SelectedVehiclesDictionary) => {
            this._selectedVehicles = selectedVehicles;

            // Update selection state in raw data
            if (this._rawItemsLate) {
                this._rawItemsLate = _.map(this._rawItemsLate, item => {
                    item.selected = _.includes(selectedVehicles?.allValues, item.id);
                    return item;
                });
            }

            if (this._rawItemsEarly) {
                this._rawItemsEarly = _.map(this._rawItemsEarly, item => {
                    item.selected = _.includes(selectedVehicles?.allValues, item.id);
                    return item;
                });
            }

            // Re-apply filter to update displayed items with new selection state
            this._applyFilter();
        });
    }
}

export class PunctualitySelectionOverviewComponentState {
    public columnsLate: CompactListColumn[];
    public columnsEarly: CompactListColumn[];
    public selectedEarlyDeviationMinutes: number;
    public selectedEarlyDeviationSeconds: number;
    public selectedLateDeviationMinutes: number;
    public selectedLateDeviationSeconds: number;
    public selectedStopFilter: FilterStopValue;
}
